import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeReopenOnReply,
  changeReopenRuleStatus,
  createReopenRule,
  removeReopenRule,
  reopenListQuery,
  reopenShellQuery,
  reopenTypeStatusesQuery,
  type ReopenRule,
} from '../../api/settingsEaseedeskReopen'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Reopen ticket.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_reopen/index.php` + `list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/tickets/ticket_reopen/index-1.js` si
 * `list-1.js`: aceleasi sase coloane, acelasi modal, aceleasi doua comutatoare,
 * aceeasi intrebare inainte de stergere.
 *
 * CATE O REGULA PE TIP DE TICHET
 * ------------------------------
 *
 * Regula spune in ce STARE intra un tichet cand se redeschide. Al doilea
 * comutator - "Reopen ticket on email notification reply" - e o intrebare
 * separata: redeschide-l si cand clientul raspunde la e-mailul de instiintare.
 * In portal statea pe o casuta iCheck cu alt cap de salvare, si asa ramane.
 *
 * Un tip de tichet nu poate avea doua reguli. A doua incercare se intoarce cu
 * mesajul serverului, si el se arata ca atare - e singurul loc unde omul afla
 * de ce nu s-a scris.
 */
function ReopenRowCells({
  rule,
  onReload,
}: {
  rule: ReopenRule
  onReload(): void
}) {
  const flip = useMutation({
    mutationFn: (v: { what: 'status' | 'reply'; on: boolean }) =>
      v.what === 'status' ? changeReopenRuleStatus(rule.ID, v.on) : changeReopenOnReply(rule.ID, v.on),
    onSuccess: (_data, v) => {
      toast(v.what === 'status' ? 'Setting changed' : 'Setting changed.', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeReopenRule(rule.ID),
    onSuccess: () => {
      toast('Setting deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td>
        <span className="badge pull-left" style={{ minWidth: 23, backgroundColor: rule.color_identifier, color: '#fff' }}>
          {rule.short_name}
        </span>
      </td>
      <td>{rule.type_name}</td>
      <td>{rule.status_name}</td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="active"
          checked={rule.active === 1}
          onChange={(e) => flip.mutate({ what: 'status', on: e.target.checked })}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          className="icheck-reply"
          value={rule.ID}
          checked={rule.reopen_on_reply === 1}
          onChange={(e) => flip.mutate({ what: 'reply', on: e.target.checked })}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <div className="btn-group" role="group" aria-label="controls">
          <button
            type="button"
            className="btn btn-danger btn-xs delete-setting"
            data-setting={rule.ID}
            onClick={() => {
              if (!window.confirm('Delete this setting?\nYou will not be able to recover this setting!')) return
              remove.mutate()
            }}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </td>
    </tr>
  )
}

export function ReopenTicketPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(reopenShellQuery())
  const list = useQuery(reopenListQuery())
  const [creating, setCreating] = useState(false)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'reopen_ticket'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Reopen</strong> ticket
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        When ticket is reopened, manage what <strong>ticket status</strong> it has
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add settings
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The list" />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th width={60}></th>
                <th style={{ textAlign: 'left' }}>Ticket type</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th>Status</th>
                <th>Reopen ticket on email notification reply</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.settings.map((rule) => (
                <ReopenRowCells key={rule.ID} rule={rule} onReload={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ReopenModal
        open={creating}
        ticketTypes={shell.data.ticketTypes}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newSettingsModal` din macheta: tip, stare, si casuta de raspuns. */
export function ReopenModal({
  open,
  ticketTypes,
  onClose,
  onSaved,
}: {
  open: boolean
  ticketTypes: { ID: number; type_name: string }[]
  onClose(): void
  onSaved(): void
}) {
  const [type, setType] = useState(0)
  const [status, setStatus] = useState(0)
  const [onReply, setOnReply] = useState(false)
  /* Ca `getTicketStatus()` din vechea macheta: starile vin din fluxul tipului
     ales, si se cer din nou la fiecare schimbare a lui. */
  const typeStatuses = useQuery(reopenTypeStatusesQuery(type))
  const statuses = type > 0 ? (typeStatuses.data?.statuses ?? []) : []

  useEffect(() => {
    if (!open) return
    setType(0)
    setStatus(0)
    setOnReply(false)
  }, [open])

  const save = useMutation({
    mutationFn: () => createReopenRule(type, status, onReply),
    onSuccess: () => {
      toast('Setting created', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newSettingsModal"
      labelledBy="newSettingsModalLabel"
      open={open}
      onClose={onClose}
      title="New settings"
    >
      <form
        id="new-settings-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (type < 1 || status < 1) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="ticket-type" className="control-label">
            Ticket type:
          </label>
          <select
            className="form-control"
            id="ticket-type"
            name="ticket_type"
            value={type}
            onChange={(e) => {
              setType(Number(e.target.value))
              setStatus(0)
            }}
            required
          >
            <option value={0}>Select</option>
            {ticketTypes.map((t) => (
              <option key={t.ID} value={t.ID}>
                {t.type_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="ticket-status" className="control-label">
            Status:
          </label>
          <select
            className="form-control"
            id="ticket-status"
            name="ticket_status"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {statuses.map((s) => (
              <option key={s.ID} value={s.ID}>
                {s.status_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="reopen-on-reply" className="control-label">
            Reopen ticket on email notification reply:
          </label>{' '}
          <input
            type="checkbox"
            id="reopen-on-reply"
            className="icheck"
            name="reopen_on_reply"
            checked={onReply}
            onChange={(e) => setOnReply(e.target.checked)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-settings"
            disabled={type < 1 || status < 1 || save.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}
